// Codec for (de)serializing condition AST expressions used in Workflows module.
// Mirrors the behaviour of backend/components/workflows/.../codec.rb but using
// the GraphQL-generated TypeScript types so that consumers only deal with those
// familiar types (WorkflowsExpression, WorkflowsLogical, etc.).
//
// The persisted JSON format uses a small discriminator key (`_type`) to identify
// each node kind. That same format is produced/consumed by the backend and must
// remain stable.
//
// The codec intentionally avoids class wrappers to prevent confusion for
// consumers – everything is typed via existing GraphQL interfaces and plain
// helper functions.
import { WorkflowsValueObjectsConditionAstTermsLiteralTypeEnumValues as LITERAL_VALUES } from 'generated/resources/datatable.graphql'
import {
  WorkflowsValueObjectsConditionAstTermsLogicalOperatorEnumValues as LOGICAL_OP_VALUES,
  WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnumValues as REL_OP_VALUES,
} from 'generated/resources/datatable.graphql'
import {
  WorkflowsValueObjectsConditionAstTermsLiteralTypeEnum as LiteralTypeEnum,
  WorkflowsValueObjectsConditionAstTermsLogicalOperatorEnum as LogicalOpEnum,
  WorkflowsValueObjectsConditionAstTermsRelationalOperatorEnum as RelOpEnum,
} from 'generated/resources/graphql'
import { z } from 'zod'

import {
  WorkflowsComparison,
  WorkflowsExpression,
  WorkflowsLiteral,
  WorkflowsLogical,
  WorkflowsVariable,
} from 'modules/workflows/lib/types'

/** Key used by backend to discriminate node kinds inside JSON hashes */
const TYPE_KEY = '_type'

/** Sets enum values for literal types, relational operators, and logical operators */
const LITERAL_TYPE_SET = new Set<string>(LITERAL_VALUES) // string, boolean, etc.
const REL_OP_SET = new Set<string>(REL_OP_VALUES) // ==, !=, etc.
const LOGICAL_OP_SET = new Set<string>(LOGICAL_OP_VALUES) // AND, OR, etc.

// -----------------------------------------------------------------------------
// Zod schemas for raw JSON nodes (schema type + discriminator)
// -----------------------------------------------------------------------------

const ZVariableNode = z.object({
  [TYPE_KEY]: z.literal('variable'),
  name: z.string(),
  source: z.enum(['input', 'dynamic_input']),
})

const ZLiteralNode = z.object({
  [TYPE_KEY]: z.literal('literal'),
  value: z.string(),
  type: z.custom<LiteralTypeEnum>((v) => typeof v === 'string' && LITERAL_TYPE_SET.has(v)),
})

const ZComparisonNode = z.object({
  [TYPE_KEY]: z.literal('comparison'),
  left: ZVariableNode,
  operator: z.custom<RelOpEnum>((v) => typeof v === 'string' && REL_OP_SET.has(v)),
  right: ZLiteralNode,
})

const ZLogicalNode = z.object({
  [TYPE_KEY]: z.literal('logical'),
  operator: z.custom<LogicalOpEnum>((v) => typeof v === 'string' && LOGICAL_OP_SET.has(v)),
  expressions: z.array(ZComparisonNode),
})

const ZExpression = z.object({
  root: ZLogicalNode,
})

// -----------------------------------------------------------------------------
// Error type
// -----------------------------------------------------------------------------
class InvalidWorkflowExpression extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidWorkflowExpression'
  }
}

// -----------------------------------------------------------------------------
// Serialization (GraphQL → raw JSON objects)
// -----------------------------------------------------------------------------
function serializeVariable(variable: WorkflowsVariable): unknown {
  return {
    [TYPE_KEY]: 'variable',
    name: variable.name,
    source: variable.source,
  }
}

function serializeLiteral(literal: WorkflowsLiteral): unknown {
  return {
    [TYPE_KEY]: 'literal',
    value: literal.value,
    type: literal.type,
  }
}

function serializeComparison(comparison: WorkflowsComparison): unknown {
  return {
    [TYPE_KEY]: 'comparison',
    left: serializeVariable(comparison.left),
    operator: comparison.operator,
    right: serializeLiteral(comparison.right),
  }
}

function serializeLogical(logical: WorkflowsLogical): unknown {
  return {
    [TYPE_KEY]: 'logical',
    operator: logical.operator,
    expressions: logical.expressions.map(serializeComparison),
  }
}

// -----------------------------------------------------------------------------
// Deserialization (raw JSON objects → GraphQL shaped objects) using zod
// -----------------------------------------------------------------------------

function deserializeComparison(node: unknown): WorkflowsComparison {
  const parsed = ZComparisonNode.safeParse(node)
  if (!parsed.success) throw new InvalidWorkflowExpression('Comparison node invalid')
  return {
    left: { name: parsed.data.left.name, source: parsed.data.left.source },
    operator: parsed.data.operator,
    right: { value: parsed.data.right.value, type: parsed.data.right.type },
  }
}

function deserializeLogical(node: unknown): WorkflowsLogical {
  const parsed = ZLogicalNode.safeParse(node)
  if (!parsed.success) throw new InvalidWorkflowExpression('Logical node invalid')
  return {
    operator: parsed.data.operator,
    expressions: parsed.data.expressions.map(deserializeComparison),
  }
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export function deserializeExpression(raw: unknown): WorkflowsExpression {
  const parsed = ZExpression.safeParse(raw)
  if (!parsed.success) {
    throw new InvalidWorkflowExpression('Expression must contain logical root')
  }
  return { root: deserializeLogical(parsed.data.root) }
}

export function serializeExpression(expr: WorkflowsExpression): unknown {
  return {
    root: serializeLogical(expr.root),
  }
}
